import { PayloadAction } from "@reduxjs/toolkit";
import api from "../../Axios/middleware";
import { AxiosResponse } from "axios";
import { call, put } from "redux-saga/effects";
import { successLogin, failureLogin, forgotPassStart, successforgotPass, failureforgotPass } from "./LoginSlicer";
import { IAPIResponse } from "../../interfaces/IAPIResponse";
import { ILoginResponse } from "../../../Pages/Login/ILogin";
import { encode } from "../../../Utils/encodeDecode";

/**
 * Owner Login API - Calls /parkingOwner/login endpoint
 */
const LoginApi = async (action: PayloadAction<{ email: string, password: string }>) => {
  const { email, password } = action.payload;
  const response: AxiosResponse = await api.post('/parkingOwner/login', {
    email,
    password
  });
  return response;
};

export function* LoginCall(
  action: PayloadAction<{ email: string; password: string }>
) {
  try {
    const response: AxiosResponse = yield call(LoginApi, action);
    
    // Extract data from API response
    const responseData = response.data.response;
    
    if (responseData.status && responseData.data) {
      const { token, user } = responseData.data;
      
      // Store token in localStorage
      localStorage.setItem('token', token);
      
      // Store user data
      const userData = {
        accessToken: token,
        email: user.email,
        uid: user.uid,
        user: {
          id: user._id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          profilePicture: '',
          role_id: user.role || 'owner',
        },
        stsTokenManager: {},
        forgotpass: false
      };
      
      localStorage.setItem('user', encode(userData));
      localStorage.setItem('role', user.role || user.userType || 'owner');
      
      // Update successLogin to match expected structure
      yield put(successLogin(userData));
    } else {
      throw new Error(responseData.message || 'Login failed');
    }
  } catch (error: any) {
    console.log('Login error:', error);
    
    // Extract error message from various possible locations
    // Backend returns: { responseCode: 401, responseMessage: 'failed', response: { error: '...' } }
    let errorMessage = 'Login failed. Please check your credentials.';
    
    if (error?.response?.data) {
      // Check if error is in response.response.error (most common)
      if (error.response.data.response?.error) {
        errorMessage = error.response.data.response.error;
      }
      // Check if error is directly in response.data.error
      else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      // Check if error is in response.data.response.message
      else if (error.response.data.response?.message) {
        errorMessage = error.response.data.response.message;
      }
      // Check if error is a string directly
      else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    }
    // Fallback to error message
    else if (error?.message) {
      errorMessage = error.message;
    }
    
    // Get response code from error response or default to 401
    const responseCode = error?.response?.data?.responseCode || 
                        error?.response?.status || 
                        401;
    
    yield put(failureLogin({
      responseCode: responseCode,
      responseMessage: 'failed',
      response: { error: errorMessage }
    }));
  }
}

/**
 * Forgot Password API - Check email exists and is active
 */
const ForgotApi = async (action: PayloadAction<{ email: string }>) => {
  const { email } = action.payload;
  const response: AxiosResponse = await api.post('/parkingOwner/forgot-password', { email });
  return response;
};

export function* ForgotCall(
  action: PayloadAction<{ email: string }>
) {
  try {
    const respons: AxiosResponse = yield call(
      ForgotApi,
      action
    );
    yield put(successforgotPass({
      responseCode: 200, 
      responseMessage: "success", 
      status: true, 
      response: { 
        message: respons.data.response?.message || "Email verified. You can now reset your password." 
      }
    }));
  } catch (error: any) {
    console.log(error);
    
    // Extract error message
    let errorMessage = 'Failed to verify email. Please try again.';
    
    if (error?.response?.data) {
      if (error.response.data.response?.error) {
        errorMessage = error.response.data.response.error;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.response?.message) {
        errorMessage = error.response.data.response.message;
      } else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    const responseCode = error?.response?.data?.responseCode || 
                        error?.response?.status || 
                        400;
    
    yield put(failureforgotPass({
      responseCode: responseCode,
      responseMessage: 'failed',
      response: { error: errorMessage }
    }));
  }
}